<template>
  <div>
    <Header :self="self" />
    <div class="container container-boards">
      <div class="alert alert-danger" role="alert" v-if="error">
        {{error.message}}
      </div>
      <div class="text-center p-3">
        <h4>
          {{user.name}} <span class="username pb-3">@{{user.username}}</span>
          <span class="p-1" v-show="self.userId && user.userId != self.userId && !user.following"><button class="btn btn-sm btn-primary" @click="follow(user)" :disabled="submitting.follow"><i class="fas fa-user-plus"></i> Follow</button></span>
          <span class="p-1" v-show="self.userId && user.following"><button class="btn btn-sm btn-light" @click="unfollow(user)" :disabled="submitting.follow"><i class="fas fa-check"></i> Following</button></span>
          <span class="p-1" v-show="!self.userId" v-b-modal.modal-create-account-follow-user><button class="btn btn-sm btn-primary"><i class="fas fa-user-plus"></i> Follow</button></span>
          <span class="p-1 btn btn-light btn-sm" v-if="self.userId && user.userId == self.userId" v-b-modal.modal-board-create><i class="fas fa-plus"></i> New Board</span>
        </h4>

        <div class="sub" v-if="user.userId">
          <span class="p-2"><strong>{{user.followerCount.toLocaleString()}}</strong> <span v-if="user.followerCount === 1">Follower</span><span v-if="user.followerCount !== 1">Followers</span></span>
          <span class="p-2"><strong>{{user.followingCount.toLocaleString()}}</strong> Following</span>
        </div>
      </div>
      <div class="d-flex flex-row justify-content-center">
        <div class="boards-wrapper d-flex flex-wrap justify-content-left">
          <div v-for="(board, index) in boards.data" class="boards">
            <nuxt-link :to="{ name: 'board-boardId-boardSlug', params: { boardId: board.boardId, boardSlug: board.slug } }">
              <img :src="board.previewImageUrls.square" v-if="board.previewImageUrls" />
            </nuxt-link>
            <div class="board-title mb-4">
              <nuxt-link :to="{ name: 'board-boardId-boardSlug', params: { boardId: board.boardId, boardSlug: board.slug } }">{{board.name}}</nuxt-link>
              <div class="sub">
                <span>{{board.itemCount}} <span v-if="board.itemCount === 1">Quote</span><span v-if="board.itemCount !== 1">Quotes</span></span>
                <span v-if="board.private" class="text-muted" title="This board is private">&nbsp;<i class="fas fa-lock"></i></span>
                <span v-if="board.hidden && !board.private" class="text-muted" title="This board is hidden">&nbsp;<i class="fas fa-eye-slash"></i></span>
                <span v-if="board.userCount > 1" class="text-muted" title="This is a group board">&nbsp;<i class="fas fa-user-friends"></i></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="modal-board-create" scrollable>
      <div class="alert alert-danger" role="alert" v-if="modalError">
        {{modalError.message}}
      </div>
      <template v-slot:modal-title>
        Create a Board
      </template>
      <div class="form-group" v-bind:class="{ 'form-group-error': $v.newBoard.name.$error }">
        <label for="name">Name</label>
        <input type="text" v-model.trim.lazy="$v.newBoard.name.$model" class="form-control" id="name" placeholder="What do you want to call this board?">
        <div class="error-input">Please provide a name</div>
      </div>
      <div class="form-check">
        <input type="checkbox" class="form-check-input" id="private" v-model="newBoard.private">
        <label class="form-check-label" for="private">Make this a private board</label>
        <small class="form-text text-muted">Private boards can only be seen and accessed by you and others you invite to the board.</small>
      </div>
      <div class="form-check" v-show="!newBoard.private">
        <input type="checkbox" class="form-check-input" id="hidden" v-model="newBoard.hidden">
        <label class="form-check-label" for="hidden">Don't show this board with my profile</label>
        <small class="form-text text-muted">Others will be able to access this board, but it will not be listed on your profile page.</small>
      </div>
      <template v-slot:modal-footer="{ ok, cancel, hide }">
        <div>
          <button class="btn btn-light btn-sm" @click="cancel()">Cancel</button>
        </div>
        <div>
          <button class="btn btn-success btn-sm ml-2" @click="createBoard()" :disabled="submitting.createBoard"><i class="fas fa-check"></i> Save</button>
        </div>
      </template>
    </b-modal>

    <b-modal id="modal-create-account-follow-user" title="Follow User" hide-footer>
      <div class="alert alert-danger" role="alert" v-if="modalError">
        {{modalError.message}}
      </div>
      <p class="text-center"><i class="fas fa-user-friends fa-5x text-primary"></i></p>
      <p class="my-4">
        <h5 class="text-center">Want to follow {{user.name}}?</h5>
      </p>
      <p class="my-4 text-center">
        Join Quozio to follow others, save & share your favorite quotes and more. It's free!
      </p>
      <p class="my-4 text-center">
        <router-link class="btn btn-success btn-lg" :to="{ name: 'join', query: { redirect: getRoutePath() } }">Join Now, It's Free!</router-link>
      </p>
      <p class="my-4 text-center">
        Already have an account? <router-link :to="{ name: 'sign-in', query: { redirect: getRoutePath() } }" v-cloak>Sign In</router-link>.
      </p>
    </b-modal>
    <Footer />
  </div>
</template>

<script>
  import Header from "@/components/header";
  import Footer from "@/components/footer";
  import { mapState, mapActions } from "vuex";
  import { required } from "vuelidate/lib/validators";
  const boardLimit = 25; // You need enough items that a scrollbar will be generated for the page so that new items will trigger when they scroll to the bottom.
  let isScrolling;

  export default {
    name: "App",
    components: { Header, Footer },
    data() {
      return {
        user: {},
        boards: { data: [] },
        loadingBoards: false,
        newBoard: { name: null, private: false, hidden: false },
        error: null,
        modalError: null,
        submitting: { follow: false, createBoard: false },
      }
    },
    head() {
      return {
        title: `${this.user.name} (@${this.user.username}) on Quozio`
      }
    },
    async asyncData({ store, app, params, error, redirect, $config }) {
      await store.dispatch("self/get", app.$cookies);
      let userResponse = await app.$dataService.get(`/users/${params.username}`);
      if (userResponse.error) {
        return error({ statusCode: userResponse.error.status, message: userResponse.error.message, reference: userResponse.error.reference });
      }

      // Make sure the username matches case
      if (userResponse.data.username != params.username) {
        return redirect(301, `${$config.BASE_URL}/${userResponse.data.username}`);
      }

      let boardsParams = { userId: params.username, includePreviewImages: true, limit: boardLimit };
      let boardsResponse = await app.$dataService.get("/boards", boardsParams);
      if (boardsResponse.error) {
        this.error = boardsResponse.error;
        return;
      }

      return { user: userResponse.data, boards: boardsResponse.data };
    },
    async mounted() {

      // Only get the user again if you have a token. This allow you to check the follow status of the user making the request.
      if (this.$cookies.get("token")) {
        let userResponse = await this.$dataService.get(`/users/${this.$route.params.username}`);
        if (userResponse.error) {
          this.error = userResponse.error;
          return;
        }
        this.user = userResponse.data;
      }

      this.$root.$on("bv::modal::hidden", (bvEvent, modalId) => {
        this.modalError = null;
      });

      window.addEventListener("scroll", this.checkLoadBoards, false);

    },
    methods: {
      async getMoreBoards(vm) {
        if (vm.boards && vm.boards.pageNext) {
          vm.loadingBoards = true;
          console.log(vm.boards.pageNext);
          let response = await this.$dataService.get(vm.boards.pageNext);
          if (response.error) {
            this.error = response.error;
            return;
          }
          let newItems = response.data;

          vm.boards.data = vm.boards.data.concat(newItems.data);
          vm.boards.pageNext = newItems.pageNext;
          vm.loadingBoards = false;
        }
      },
      async follow(user) {
        this.submitting.follow = true;
        user.following = true;
        this.$nuxt.$loading.start();
        let response = await this.$dataService.put(`/users/me/follows/${user.userId}`);
        this.submitting.follow = false;
        this.$ga.event("User", "Follow - Started", user.userId);
        if (response.error) {
          this.error = response.error;
          this.$nuxt.$loading.finish();
          user.following = false;
          return;
        }

        user.followerCount++;
        this.$nuxt.$loading.finish();
      },
      async unfollow(user) {
        this.submitting.follow = true;
        user.following = false;
        this.$nuxt.$loading.start();
        let response = await this.$dataService.delete(`/users/me/follows/${user.userId}`);
        this.submitting.follow = false;
        this.$ga.event("User", "Follow - Ended", user.userId);
        if (response.error) {
          this.error = response.error;
          this.$nuxt.$loading.finish();
          user.following = true;
          return;
        }

        user.followerCount--;
        this.$nuxt.$loading.finish();
      },
      async createBoard() {
        this.submitting.createBoard = true;
        this.$nuxt.$loading.start();
        if (this.newBoard.private) {
          this.newBoard.hidden = true;
        }
        let response = await this.$dataService.post("/boards", this.newBoard, { includePreviewImages: true });
        this.submitting.createBoard = false;
        if (response.error) {
          this.modalError = response.error;
          this.$nuxt.$loading.finish();
          return;
        }
        this.$ga.event("Board", "Board Created", response.data.boardId, response.data.private ? 0 : 1);
        this.$bvModal.hide("modal-board-create");
        this.boards.data.unshift(response.data);
        this.newBoard = { name: null, private: false, hidden: false };
        this.$v.$reset();
        this.$nuxt.$loading.finish();
      },
      checkLoadBoards() {
        window.clearTimeout(isScrolling);
        let vm = this;
        isScrolling = setTimeout(function () {
          let el = document.getElementsByTagName("HTML")[0];
          if (!vm.loadingBoards && el && el.scrollHeight - el.scrollTop - el.clientHeight < 300) {
            vm.getMoreBoards(vm);
          }
        }, 75);
      },
      getRoutePath() {
        if (this && this.$route) {
          return this.$route.path;
        }
      },
      ...mapActions({ getSelf: "self/get" }),
    },
    beforeDestroy: function () {
      window.removeEventListener("scroll", this.checkLoadBoards, false);
    },
    computed: {
      ...mapState("self", { self: "self" }),
    },
    validations: {
      newBoard: {
        name: {
          required
        }
      }
    }
  };
</script>
